<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import {
  asArray,
  isActiveKycVerification,
  kycAttemptRows,
  kycDocumentRows,
  kycEvidenceRows,
  kycRejectionReason,
  kycStatusClass,
  kycStatusLabel,
  kycVerificationSummaryRows,
} from '../utils/kycOfferHistory';

const props = defineProps({
  verifications: { type: Array, default: () => [] },
});

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const scoreLabel = verification => {
  const score = Number(verification?.score);
  if (!Number.isFinite(score)) return '';
  return `${Math.round(score * 100)}%`;
};

const cards = computed(() =>
  asArray(props.verifications).map((verification, index) => ({
    verification,
    key: verification?.id || `${verification?.status || 'kyc'}-${index}`,
    title: `Tentativa ${index + 1}`,
    statusLabel: kycStatusLabel(verification?.status),
    statusClass: kycStatusClass(verification?.status),
    isActive: isActiveKycVerification(verification),
    summaryRows: kycVerificationSummaryRows(verification),
    documentRows: kycDocumentRows(verification),
    attemptRows: kycAttemptRows(verification),
    evidenceRows: kycEvidenceRows(verification),
    reason: kycRejectionReason(verification),
    score: scoreLabel(verification),
    timestamp:
      verification?.verified_at ||
      verification?.updated_at ||
      verification?.created_at,
  }))
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-3">
    <div
      v-if="!cards.length"
      class="rounded-lais-sm border border-dashed border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] leading-relaxed text-lais-text-muted"
    >
      Nenhuma verificação KYC registrada para esta oferta.
    </div>

    <article
      v-for="card in cards"
      :key="card.key"
      class="grid gap-3 rounded-lais-sm border border-lais-border-soft bg-white px-3 py-3"
    >
      <header class="flex flex-wrap items-start justify-between gap-2">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <p class="font-semibold text-lais-text">{{ card.title }}</p>
            <span
              v-if="card.isActive"
              class="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700"
            >
              Tentativa ativa
            </span>
          </div>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            {{ formatDate(card.timestamp) }}
          </p>
        </div>
        <span
          class="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase"
          :class="card.statusClass"
        >
          {{ card.statusLabel }}
        </span>
      </header>

      <div class="grid gap-2 text-[13px]">
        <div
          v-for="row in card.summaryRows"
          :key="row.label"
          class="flex justify-between gap-3"
        >
          <span class="text-lais-text-muted">{{ row.label }}</span>
          <span class="text-right font-semibold text-lais-text">
            {{ row.date ? formatDate(row.value) : row.value }}
          </span>
        </div>
        <div v-if="card.score" class="flex justify-between gap-3">
          <span class="text-lais-text-muted">Score</span>
          <span class="font-semibold text-lais-text">{{ card.score }}</span>
        </div>
      </div>

      <div
        v-if="card.reason"
        class="rounded-lais-sm border border-rose-100 bg-rose-50 px-3 py-2 text-[12px] leading-relaxed text-rose-700"
      >
        <p class="font-semibold">Causa da rejeição</p>
        <p class="mt-1">{{ card.reason }}</p>
      </div>

      <div class="grid gap-2">
        <div class="flex items-center justify-between gap-3">
          <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
            Documentos redigidos
          </p>
          <span class="text-[12px] font-semibold text-lais-text-muted">
            {{ card.documentRows.length }}
          </span>
        </div>
        <div v-if="card.documentRows.length" class="grid gap-2">
          <div
            v-for="document in card.documentRows"
            :key="document.key"
            class="flex items-start justify-between gap-3 rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-[12px]"
          >
            <div class="min-w-0">
              <p class="font-semibold text-lais-text">{{ document.label }}</p>
              <p class="mt-0.5 truncate text-lais-text-muted">
                {{ document.filename || document.hashLabel }}
              </p>
            </div>
            <span class="shrink-0 font-semibold text-lais-text-muted">
              {{ document.redacted ? 'Redigido' : document.status }}
            </span>
          </div>
        </div>
        <p
          v-else
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-[12px] text-lais-text-muted"
        >
          Sem documentos persistidos para esta tentativa.
        </p>
      </div>

      <div class="grid gap-2">
        <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
          Histórico de tentativas
        </p>
        <div v-if="card.attemptRows.length" class="grid gap-2">
          <div
            v-for="attempt in card.attemptRows"
            :key="attempt.key"
            class="grid gap-1 rounded-lais-sm border border-lais-border-soft px-3 py-2 text-[12px]"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="font-semibold text-lais-text">{{ attempt.label }}</p>
              <span
                class="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                :class="attempt.statusClass"
              >
                {{ attempt.status }}
              </span>
            </div>
            <p class="text-lais-text-muted">
              {{ formatDate(attempt.occurredAt) }}
              <span v-if="attempt.responsible">
                · {{ attempt.responsible }}
              </span>
            </p>
            <p v-if="attempt.error" class="text-rose-700">
              {{ attempt.error }}
            </p>
          </div>
        </div>
        <p
          v-else
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-[12px] text-lais-text-muted"
        >
          Sem tentativas auditadas retornadas pelo provider.
        </p>
      </div>

      <div class="grid gap-2">
        <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
          Evidência redigida
        </p>
        <div v-if="card.evidenceRows.length" class="grid gap-1 text-[12px]">
          <div
            v-for="evidence in card.evidenceRows"
            :key="`${evidence.label}-${evidence.value}`"
            class="flex justify-between gap-3 rounded-lais-sm bg-lais-bg-soft px-3 py-1.5"
          >
            <span class="truncate text-lais-text-muted">
              {{ evidence.label }}
            </span>
            <span
              class="max-w-[55%] truncate text-right font-semibold text-lais-text"
            >
              {{ evidence.value }}
            </span>
          </div>
        </div>
        <p
          v-else
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-[12px] text-lais-text-muted"
        >
          Evidência sensível redigida pelo backend.
        </p>
      </div>
    </article>
  </div>
</template>
